import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, Tag, User as UserIcon, FileText, CircleNotch, Crown } from "@phosphor-icons/react";
import api from "../lib/api";
import { Input } from "./ui/input";

/**
 * Feed-page search bar.
 *
 * Renders as a magnifying-glass icon button. On click it expands to
 * a text input which live-searches the `/api/search` endpoint and
 * displays a grouped dropdown (Posts / People / Categories).
 *
 * Click a result → navigate to the appropriate route:
 *   • post     → /post/:id
 *   • person   → /feed?author_id=:id (filters feed to their posts)
 *   • category → /category/:slug
 */
export default function SearchBar() {
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();

    // Focus input the moment the field appears (post CSS transition).
    useEffect(() => {
        if (expanded && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [expanded]);

    // Debounced search — waits 300ms after keystroke before hitting the API.
    useEffect(() => {
        const term = query.trim();
        if (term.length < 2) {
            setResults(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/search", { params: { q: term, limit: 5 } });
                setResults(data);
            } catch {
                setResults({ posts: [], people: [], categories: [], q: term });
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    // Close on click-outside.
    useEffect(() => {
        if (!expanded) return;
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                collapse();
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [expanded]);

    // Close on Escape.
    useEffect(() => {
        if (!expanded) return;
        const onKey = (e) => {
            if (e.key === "Escape") collapse();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [expanded]);

    const collapse = useCallback(() => {
        setExpanded(false);
        setQuery("");
        setResults(null);
    }, []);

    const openPost = (id) => { collapse(); navigate(`/post/${id}`); };
    const openPerson = (id) => { collapse(); navigate(`/feed?author_id=${id}`); };
    const openCategory = (slug) => { collapse(); navigate(`/category/${slug}`); };

    const totalHits = useMemo(() => {
        if (!results) return 0;
        return (results.posts?.length || 0) + (results.people?.length || 0) + (results.categories?.length || 0);
    }, [results]);

    const showDropdown = expanded && query.trim().length >= 2;

    return (
        <div ref={wrapperRef} className="relative" data-testid="feed-search-wrapper">
            {!expanded ? (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="h-10 w-10 flex items-center justify-center rounded-full border border-border bg-card hover:bg-accent transition-colors"
                    aria-label="Search"
                    data-testid="feed-search-toggle"
                >
                    <MagnifyingGlass size={18} weight="regular" />
                </button>
            ) : (
                <div className="flex items-center gap-2" data-testid="feed-search-expanded">
                    <div className="relative w-64 sm:w-80">
                        <MagnifyingGlass
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                        />
                        <Input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search ideas, people, categories…"
                            className="pl-9 pr-9 h-10 rounded-full"
                            data-testid="feed-search-input"
                        />
                        {loading && (
                            <CircleNotch
                                size={16}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin"
                            />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={collapse}
                        className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent transition-colors"
                        aria-label="Close search"
                        data-testid="feed-search-close"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {showDropdown && (
                <div
                    className="absolute right-0 top-12 z-40 w-[92vw] sm:w-[420px] max-h-[70vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-xl"
                    data-testid="feed-search-dropdown"
                >
                    {results === null || (loading && totalHits === 0) ? (
                        <div className="p-6 text-sm text-muted-foreground text-center" data-testid="feed-search-loading">
                            Searching…
                        </div>
                    ) : totalHits === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center" data-testid="feed-search-empty">
                            No matches for “{results.q}”. Try a different word.
                        </div>
                    ) : (
                        <>
                            {results.posts && results.posts.length > 0 && (
                                <SearchSection title="Feed" icon={<FileText size={14} />}>
                                    {results.posts.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => openPost(p.id)}
                                            className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-accent transition-colors border-b border-border/60 last:border-b-0"
                                            data-testid={`search-result-post-${p.id}`}
                                        >
                                            {p.image_url ? (
                                                <img
                                                    src={p.image_url}
                                                    alt=""
                                                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6C63FF]/20 to-[#FF8A00]/20 flex items-center justify-center flex-shrink-0">
                                                    <FileText size={16} className="text-[#6C63FF]" />
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold truncate">{p.title}</div>
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {p.category} · {p.country || "Global"} · by {p.author_name || "Anonymous"}
                                                </div>
                                                {p.teaser && (
                                                    <div className="text-xs text-muted-foreground/80 mt-1 line-clamp-1">
                                                        {stripMarkdown(p.teaser)}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </SearchSection>
                            )}

                            {results.people && results.people.length > 0 && (
                                <SearchSection title="People" icon={<UserIcon size={14} />}>
                                    {results.people.map((u) => (
                                        <button
                                            key={u.id}
                                            type="button"
                                            onClick={() => openPerson(u.id)}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors border-b border-border/60 last:border-b-0"
                                            data-testid={`search-result-person-${u.id}`}
                                        >
                                            {u.avatar ? (
                                                <img
                                                    src={u.avatar}
                                                    alt=""
                                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#5850e0] text-white flex items-center justify-center font-bold flex-shrink-0">
                                                    {(u.name || "?").slice(0, 1).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                                                    {u.name}
                                                    {u.subscription === "pro" && (
                                                        <Crown size={12} weight="fill" className="text-[#FF8A00]" />
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {u.post_count} idea{u.post_count === 1 ? "" : "s"}
                                                    {u.country && ` · ${u.country}`}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </SearchSection>
                            )}

                            {results.categories && results.categories.length > 0 && (
                                <SearchSection title="Categories" icon={<Tag size={14} />}>
                                    {results.categories.map((c) => (
                                        <button
                                            key={c.id || c.slug}
                                            type="button"
                                            onClick={() => openCategory(c.slug)}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors border-b border-border/60 last:border-b-0"
                                            data-testid={`search-result-category-${c.slug}`}
                                        >
                                            <div className="w-10 h-10 rounded-lg bg-[#FF8A00]/10 text-[#FF8A00] flex items-center justify-center flex-shrink-0">
                                                <Tag size={16} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold truncate">{c.name}</div>
                                                <div className="text-xs text-muted-foreground">Explore all ideas in {c.name}</div>
                                            </div>
                                        </button>
                                    ))}
                                </SearchSection>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SearchSection({ title, icon, children }) {
    return (
        <div className="border-b border-border last:border-b-0">
            <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                {icon}
                {title}
            </div>
            <div>{children}</div>
        </div>
    );
}

function stripMarkdown(s) {
    // Very light cleanup — strip **bold** markers so teasers read cleanly.
    return (s || "").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}
